import FormErrors from './FormErrors';
import { baseApi } from 'core/api';

class Form {
    /**
     * Create a new Form instance.
     *
     * @param {Object} data
     * @param {Boolean=} persistChanges
     */
    constructor(data, persistChanges = false, useEmptyField = true) {
        data = data || {};
        this.busy = false;
        this.successful = false;
        this.original = JSON.parse(JSON.stringify(data));
        this.persistChanges = persistChanges;
        this.useEmptyField = useEmptyField;

        for (let field in data) {
            this[field] = data[field];
        }

        this.errors = new FormErrors();
    }

    /**
     * Returns the relevant data for the form.
     *
     * @return {Object}
     */
    data() {
        let data = {};

        for (let field in this.original) {
            if (this.useEmptyField) {
                data[field] = this[field];
            } else {
                if (this[field] !== '' && this[field] !== null) {
                    data[field] = this[field];
                }
            }
        }

        return data;
    }

    /**
     * Reset the form fields.
     */
    reset() {
        for (let field in this.original) {
            if (
                Object.prototype.toString.call(this[field]).slice(8, -1).toLowerCase() === 'object'
            ) {
                for (let key in this[field]) {
                    this[field][key] = this.original[field][key];
                }
            } else {
                this[field] = this.original[field];
            }
        }

        this.busy = false;
        this.successful = false;
        this.errors.clear();
    }

    /**
     * Send a GET request to the given URL.
     *
     * @param {String} url
     * @param {object} params
     * @return {Promise}
     */
    get(url, params) {
        return this.submit('get', url, params);
    }

    /**
     * Send a POST request to the given URL.
     *
     * @param {String} url
     * @param {object} params
     * @param {object} config
     * @return {Promise}
     */
    post(url, params, config) {
        return this.submit('post', url, params, config);
    }

    /**
     * Send a PUT request to the given URL.
     *
     * @param {String} url
     * @return {Promise}
     */
    put(url) {
        return this.submit('put', url);
    }

    /**
     * Send a PATCH request to the given URL.
     *
     * @param {String} url
     * @return {Promise}
     */
    patch(url) {
        return this.submit('patch', url);
    }

    /**
     * Send a DELETE request to the given URL.
     *
     * @param {String} url
     * @return {Promise}
     */
    delete(url) {
        return this.submit('delete', url);
    }

    /**
     * Send a DELETE request to the given URL with body instead of query.
     *
     * @param {String} url
     * @return {Promise}
     */
    deleteWithBody(url) {
        return this.submit('deleteWithBody', url);
    }

    /**
     * Submit the form.
     *
     * @param {String} verb
     * @param {String} url
     * @param {object} params
     * @param {object} config
     * @return {Promise}
     */
    submit(verb, url, params = null, config = null) {
        return new Promise((resolve, reject) => {
            this.busy = true;
            this.successful = false;

            let data = {};
            if (verb === 'delete' || verb === 'get') {
                data = this.data();

                Object.keys(data).map((k) => {
                    if (data[k] === true) {
                        data[k] = '1';
                    } else if (data[k] === false) {
                        data[k] = '0';
                    }
                });

                data = {
                    params: data,
                    ...params,
                };
            } else {
                if (verb === 'post' && params) {
                    data = params;
                } else {
                    data = this.data();
                }

                if (verb === 'deleteWithBody') {
                    verb = 'delete';
                    /** @see https://stackoverflow.com/questions/51069552/axios-delete-request-with-body-and-headers */
                    data = { data };
                }
            }

            baseApi[verb](url, data, config)
                .then((response) => {
                    this.onSuccess(response.data, verb);

                    return resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.data) {
                        if (error.config.responseType === 'blob') {
                            const reader = new FileReader();

                            reader.onload = () => {
                                this.onFail(JSON.parse(reader.result));
                            };

                            reader.readAsText(error.response.data);
                        } else {
                            this.onFail(error.response.data);
                        }
                    }

                    return reject(error);
                });
        });
    }

    /**
     * Handle a successful form submission.
     *
     * @param {Object} data
     * @param {String} verb
     * @return {void}
     */
    onSuccess(data, verb) {
        this.busy = false;
        this.successful = true;

        if (!this.persistChanges) {
            return;
        }

        if (verb === 'patch') {
            this.value = data[this.attribute];
            this.original.value = data[this.attribute];
        } else {
            for (let field in data) {
                if (field.indexOf('password') !== -1) {
                    continue;
                }

                this[field] = data[field];
                this.original[field] = data[field];
            }
        }
    }

    /**
     * Handles a failed form submission.
     *
     * @param {Object} errors
     */
    onFail(errors) {
        this.busy = false;
        this.successful = false;

        this.errors.record(errors);
    }

    /**
     * Returns true if the form has errors to display.
     *
     * @return {Boolean}
     */
    hasErrors() {
        return this.errors.any();
    }
}

export default Form;
